import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { ActionWithPayload } from "@obg/ngrx-utils";
import { CommonState } from "@obg/ngx.common";
import {
	AppError,
	JurisdictionSelector,
	MessageScopeType,
	MessageService,
	MessageType,
	ProductType,
} from "@obg/ngx.core";
import { PlatformService } from "@obg/ngx.global.evo";
import { RouteSelector } from "@obg/ngx.router";
import { merge, of, timer } from "rxjs";
import { catchError, filter, map, switchMap, takeUntil, withLatestFrom } from "rxjs/operators";

import { RegistrationFeatureState } from "../state";

import { SEAMLESS_REGISTRATION_ACTION_TYPE, SeamlessRegistrationAction } from "./seamless-registration.action";
import { SEAMLESS_REGISTRATION_MESSAGE_CODE } from "./seamless-registration.const";
import { CreateSeamlessRegistrationRequest, SeamlessRegistrationPayload } from "./seamless-registration.model";
import { SeamlessRegistrationSelector } from "./seamless-registration.selector";
import { SeamlessRegistrationService } from "./seamless-registration.service";

@Injectable()
export class SeamlessRegistrationEffect {
	initializeRegistration$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SEAMLESS_REGISTRATION_ACTION_TYPE.initializeRegistration),
			map((x: ActionWithPayload<SeamlessRegistrationPayload>) => x.payload),
			withLatestFrom(
				this.store.select<{ product: ProductType | undefined } | undefined>(RouteSelector.getQueryParams),
				this.store.select(JurisdictionSelector.get)
			),
			switchMap(([request, params, jurisdiction]) =>
				this.service.createRegistration(request, params?.product, jurisdiction).pipe(
					map((data) => this.action.initializeRegistrationSuccess(data)),
					catchError((error: AppError) => of(this.action.initializeRegistrationFail(error)))
				)
			)
		)
	);

	getRegistrationState$ = this.platformService.forClient(() =>
		createEffect(() =>
			this.actions$.pipe(
				ofType(SEAMLESS_REGISTRATION_ACTION_TYPE.getRegistrationState),
				switchMap(() =>
					timer(0, 5000).pipe(
						withLatestFrom(this.store.select(this.selector.getRegistrationReference())),
						map(([, reference]) => reference),
						filter((reference) => !!reference),
						switchMap((reference) =>
							this.service.getRegistrationState(reference!).pipe(
								map((response) => this.action.getRegistrationStateSuccess(response)),
								catchError((error: AppError) => of(this.action.getRegistrationStateFail(error)))
							)
						),
						takeUntil(
							this.actions$.pipe(
								ofType(
									SEAMLESS_REGISTRATION_ACTION_TYPE.stopPolling,
									SEAMLESS_REGISTRATION_ACTION_TYPE.getRegistrationStateFail,
									SEAMLESS_REGISTRATION_ACTION_TYPE.resetSeamlessRegistration
								)
							)
						)
					)
				)
			)
		)
	);

	createRegistration$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SEAMLESS_REGISTRATION_ACTION_TYPE.createRegistration),
			map((x: ActionWithPayload<CreateSeamlessRegistrationRequest>) => x.payload),
			withLatestFrom(this.store.select(this.selector.getRegistrationReference())),
			filter(([, reference]) => !!reference),
			switchMap(([request, reference]) =>
				this.service.confirmRegistration(reference!, request).pipe(
					map(() => this.action.createRegistrationSuccess()),
					catchError((error: AppError) => of(this.action.createRegistrationFail(error)))
				)
			)
		)
	);

	attachBonus$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SEAMLESS_REGISTRATION_ACTION_TYPE.attachBonus),
			map((x: ActionWithPayload<string>) => x.payload),
			withLatestFrom(this.store.select(this.selector.getRegistrationReference())),
			filter(([siteTrackingId, reference]) => !!siteTrackingId && !!reference),
			switchMap(([siteTrackingId, reference]) =>
				this.service.attachBonus(siteTrackingId, reference!).pipe(
					map(() => this.action.attachBonusSuccess()),
					catchError((error: AppError) => of(this.action.attachBonusFail(error)))
				)
			)
		)
	);

	initMessages$ = createEffect(() =>
		merge(
			this.messageService.map$({
				[SEAMLESS_REGISTRATION_ACTION_TYPE.getRegistrationStateFail]: {
					context: SEAMLESS_REGISTRATION_ACTION_TYPE.getRegistrationState,
					scope: MessageScopeType.Local,
					type: MessageType.Error,
					messageCode: SEAMLESS_REGISTRATION_MESSAGE_CODE.error.unknown,
				},
				[SEAMLESS_REGISTRATION_ACTION_TYPE.createRegistrationFail]: {
					context: SEAMLESS_REGISTRATION_ACTION_TYPE.createRegistration,
					scope: MessageScopeType.Local,
					type: MessageType.Error,
					messageCode: SEAMLESS_REGISTRATION_MESSAGE_CODE.error.notFound,
				},
				[SEAMLESS_REGISTRATION_ACTION_TYPE.initializeRegistrationFail]: {
					context: SEAMLESS_REGISTRATION_ACTION_TYPE.initializeRegistration,
					scope: MessageScopeType.Local,
					type: MessageType.Error,
					messageCode: SEAMLESS_REGISTRATION_MESSAGE_CODE.error.emailInvalid,
				},
			}),
			this.messageService.mapClearByContext$({
				[SEAMLESS_REGISTRATION_ACTION_TYPE.getRegistrationState]:
					SEAMLESS_REGISTRATION_ACTION_TYPE.getRegistrationState,
			})
		)
	);

	constructor(
		private actions$: Actions,
		private action: SeamlessRegistrationAction,
		private selector: SeamlessRegistrationSelector,
		private service: SeamlessRegistrationService,
		private store: Store<RegistrationFeatureState & CommonState>,
		private messageService: MessageService,
		private platformService: PlatformService
	) {}
}
